import { useState } from "react";
import { Check, Copy, KeyRound, ShieldCheck, Trash2, Zap } from "lucide-react";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import { id, tx } from "@instantdb/react";
import { Input, Label } from "../components/ui/index.ts";
import {
  formatApiToken,
  formatTokenIdentity,
  newApiTokenSecret,
  sha256Hex,
} from "../../../src/apiTokenUtils.ts";

type MintedToken = { name: string; token: string; isSupertoken: boolean };

const inputValue = (e: { target: unknown }) =>
  (e.target as { value: string }).value;

export const AdminApiTokens = () => {
  const [identity, setIdentity] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSupertoken, setIsSupertoken] = useState(true);
  const [error, setError] = useState("");
  const [minted, setMinted] = useState<MintedToken | null>(null);
  const [copied, setCopied] = useState(false);
  const { user } = db.useAuth();
  const tokensQuery = db.useQuery({ apiTokens: {} });

  if (tokensQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (tokensQuery.error) {
    return <div className="text-red-400 p-4">{tokensQuery.error.message}</div>;
  }

  const { apiTokens } = tokensQuery.data;

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const cleanIdentity = formatTokenIdentity(identity);
    const cleanName = name.trim();
    if (!cleanIdentity) {
      setError("Please enter a service name or user identity");
      return;
    }
    if (!cleanName) {
      setError("Please enter a token label/name");
      return;
    }
    if (apiTokens.some(({ name: existing }) => existing === cleanName)) {
      setError(
        "A token with this name already exists. Revoke it first, or pick another name.",
      );
      return;
    }
    const newId = id();
    const secret = newApiTokenSecret();
    const cleanDescription = description.trim();
    try {
      await db.transact(
        tx.apiTokens[newId].update({
          userId: cleanIdentity,
          name: cleanName,
          ...(cleanDescription ? { description: cleanDescription } : {}),
          secretHash: await sha256Hex(secret),
          unlimitedQuota: isSupertoken,
          createdAt: Date.now(),
          ...(user?.email ? { createdBy: user.email } : {}),
        }),
      );
      setIdentity("");
      setName("");
      setDescription("");
      setCopied(false);
      setMinted({
        name: cleanName,
        token: formatApiToken(newId, secret),
        isSupertoken,
      });
    } catch (err) {
      setError(
        `Failed to save: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const handleRevoke = (tokenId: string, tokenName: string) => {
    if (
      confirm(
        `Revoke token "${tokenName}"? Requests using it will fail immediately.`,
      )
    ) {
      db.transact(tx.apiTokens[tokenId].delete());
    }
  };

  const copyMinted = (token: string) =>
    navigator.clipboard.writeText(token).then(() => setCopied(true));

  return (
    <div className="space-y-8 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-xl p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <KeyRound size={20} className="text-purple-500" />
          Mint an API Token
        </h3>

        <form onSubmit={handleMint} className="space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-red-200 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <Label className="mb-2">Identity / Caller ID</Label>
            <Input
              type="text"
              placeholder="e.g. marketing-pipeline or +972501234567"
              value={identity}
              onChange={(e) => setIdentity(inputValue(e))}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              For services, enter the service name (resolves as{" "}
              <code>service:&lt;name&gt;</code>). For users, enter their phone
              number or user ID.
            </p>
          </div>

          <div>
            <Label className="mb-2">Token Name / Label</Label>
            <Input
              type="text"
              placeholder="e.g. Marketing Pipeline Prod"
              value={name}
              onChange={(e) => setName(inputValue(e))}
            />
          </div>

          <div>
            <Label className="mb-2">Description</Label>
            <Input
              type="text"
              placeholder="Who uses this token and for what purpose"
              value={description}
              onChange={(e) => setDescription(inputValue(e))}
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="supertoken"
              checked={isSupertoken}
              onChange={(e) => setIsSupertoken(e.target.checked)}
              className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-purple-600 focus:ring-purple-500"
            />
            <label
              htmlFor="supertoken"
              className="text-sm text-gray-300 flex items-center gap-1.5 cursor-pointer"
            >
              <Zap size={14} className="text-yellow-400" />
              <span className="font-medium text-white">
                Supertoken (Unlimited Quota)
              </span>
              <span className="text-gray-500 text-xs">
                — bypasses the 20 req/month developer limit and phone
                verification across all API endpoints
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 mt-4"
          >
            <KeyRound size={18} />
            Mint Token
          </button>
        </form>

        {minted && (
          <div className="mt-6 space-y-2 bg-black/50 border border-purple-500/40 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-purple-300 flex items-center gap-1.5">
                {minted.isSupertoken && (
                  <Zap size={12} className="text-yellow-400" />
                )}
                Token for{" "}
                {minted.name}. Copy it now: only its hash is stored, so it
                cannot be shown again.
              </span>
              <button
                type="button"
                onClick={() => copyMinted(minted.token)}
                className="text-gray-400 hover:text-white transition flex items-center gap-1 text-xs"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="font-mono text-xs text-gray-200 break-all select-all">
              {minted.token}
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-900 border border-white/10 rounded-xl p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center justify-between">
          <span>Active API Tokens</span>
          <span className="text-sm font-normal text-gray-500">
            {apiTokens.length} token{apiTokens.length === 1 ? "" : "s"}
          </span>
        </h3>

        {apiTokens.length === 0
          ? (
            <div className="text-center text-gray-500 py-12 border border-dashed border-white/10 rounded-lg">
              No API tokens minted yet.
            </div>
          )
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {apiTokens.map((token) => (
                <div
                  key={token.id}
                  className="bg-black/40 border border-white/10 rounded-lg p-5 flex items-start justify-between hover:border-white/20 transition-all"
                >
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-white">
                        {token.name}
                      </span>
                      {token.unlimitedQuota
                        ? (
                          <span className="inline-flex items-center gap-1 bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                            <Zap size={10} />
                            Supertoken
                          </span>
                        )
                        : (
                          <span className="inline-flex items-center gap-1 bg-gray-800 text-gray-400 border border-gray-700 text-[10px] font-medium px-2 py-0.5 rounded-full">
                            <ShieldCheck size={10} />
                            Standard Quota
                          </span>
                        )}
                    </div>
                    <div className="font-mono text-xs text-purple-400">
                      Identity: {token.userId}
                    </div>
                    {token.description && (
                      <div className="text-sm text-gray-400">
                        {token.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      Minted {new Date(token.createdAt).toLocaleDateString()}
                      {token.createdBy ? ` by ${token.createdBy}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(token.id, token.name)}
                    className="text-gray-500 hover:text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg transition"
                    title="Revoke token"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};
